/**
 * ViewToggle — segmented control to switch between Freeform and Structured
 * views of the motion prompt.
 */

import { memo } from 'react'

export type ViewMode = 'freeform' | 'structured'

interface ViewToggleProps {
  mode: ViewMode
  onChange: (mode: ViewMode) => void
  disabled?: boolean
  disabledTooltip?: string
}

export const ViewToggle = memo(function ViewToggle({
  mode,
  onChange,
  disabled = false,
  disabledTooltip,
}: ViewToggleProps) {
  const baseClass =
    'px-2 py-0.5 text-xxs uppercase tracking-wider transition-colors rounded'
  const selectedClass = 'bg-primary/20 text-primary'
  const unselectedClass = 'text-text-muted hover:text-foreground'
  const disabledClass = 'opacity-50 cursor-not-allowed'

  return (
    <div
      className="inline-flex items-center gap-0.5 bg-surface-1 border border-border-subtle rounded p-0.5"
      data-testid="av-view-toggle"
      title={disabled ? disabledTooltip : undefined}
    >
      <button
        type="button"
        onClick={() => !disabled && onChange('freeform')}
        disabled={disabled && mode !== 'freeform'}
        className={`${baseClass} ${mode === 'freeform' ? selectedClass : unselectedClass} ${
          disabled && mode !== 'freeform' ? disabledClass : ''
        }`}
        data-testid="av-view-toggle-freeform"
      >
        Freeform
      </button>
      <button
        type="button"
        onClick={() => !disabled && onChange('structured')}
        disabled={disabled}
        className={`${baseClass} ${mode === 'structured' ? selectedClass : unselectedClass} ${
          disabled ? disabledClass : ''
        }`}
        data-testid="av-view-toggle-structured"
      >
        Structured
      </button>
    </div>
  )
})
